import { useState, useEffect, useRef } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useToast } from '../../../components/Toast'
import { selectSelectedTask } from '../../../slice/taskSlice'
import {
  fetchShortcutsByTask,
  createShortcut,
  updateShortcut,
  deleteShortcut,
  updateShortcutsOrder,
} from '../../../slice/shortcutsSlice'
import DeleteModal from '../../../components/atomic/DeleteModal'
import { useThemeColor } from '../../../hooks/useThemeColor'
import { useTranslation } from '../../../i18n/LanguageContext'
import { X, Settings, Trash2, GripVertical, Info, Loader2, Zap } from 'lucide-react'

// Common emoji list for quick selection
const EMOJI_LIST = [
  '😊', '🇪🇸', '📌', '⚠️', '🔄', '🌐', '📝', '✨', '🎯', '📊',
  '💡', '🔍', '⚡', '🎨', '🏷️', '📋', '✅', '❌', '💬', '📢',
  '🔔', '📁', '📈', '📉', '🔗', '🛠️', '⚙️', '🔒', '🔓', '💎'
]

// Icon selection component
const IconPicker = ({ value, onChange, onClose, aispaceColor }) => {
  const { t } = useTranslation()
  const [activeTab, setActiveTab] = useState('emoji')
  const [customEmoji, setCustomEmoji] = useState('')
  const pickerRef = useRef(null)

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (pickerRef.current && !pickerRef.current.contains(event.target)) {
        onClose()
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [onClose])

  return (
    <div ref={pickerRef} className="absolute top-full left-0 mt-2 bg-white border border-[#E5E7EB] rounded-xl shadow-lg p-4 z-50 w-80">
      <div className="flex border-b border-[#E5E7EB] mb-3">
        <button
          className={`px-4 py-2 text-[14px] font-medium transition-colors ${
            activeTab === 'emoji'
              ? 'border-b-2 text-[#111827]'
              : 'text-[#6B7280]'
          }`}
          style={activeTab === 'emoji' ? { borderColor: aispaceColor } : {}}
          onClick={() => setActiveTab('emoji')}
        >
          {t('agentSettings.shortcutsTabEmoji')}
        </button>
        <button
          className={`px-4 py-2 text-[14px] font-medium transition-colors ${
            activeTab === 'custom'
              ? 'border-b-2 text-[#111827]'
              : 'text-[#6B7280]'
          }`}
          style={activeTab === 'custom' ? { borderColor: aispaceColor } : {}}
          onClick={() => setActiveTab('custom')}
        >
          {t('agentSettings.shortcutsTabCustom')}
        </button>
      </div>

      {activeTab === 'emoji' && (
        <div className="grid grid-cols-6 gap-1">
          {EMOJI_LIST.map((emoji, idx) => (
            <button
              key={idx}
              className={`w-10 h-10 flex items-center justify-center text-xl hover:bg-[#F3F4F6] rounded-lg transition-colors ${
                value === emoji ? 'bg-[#F3F4F6] ring-2' : ''
              }`}
              style={value === emoji ? { ringColor: aispaceColor } : {}}
              onClick={() => {
                onChange(emoji)
                onClose()
              }}
            >
              {emoji}
            </button>
          ))}
        </div>
      )}

      {activeTab === 'custom' && (
        <div className="space-y-3">
          <p className="text-[13px] text-[#6B7280]">{t('agentSettings.shortcutsCustomEmojiLabel')}</p>
          <div className="flex gap-2">
            <input
              type="text"
              value={customEmoji}
              onChange={(e) => setCustomEmoji(e.target.value)}
              className="flex-1 border border-[#E5E7EB] rounded-lg px-3 py-2 text-[14px] focus:outline-none focus:border-[var(--focus-color)]"
              style={{ '--focus-color': aispaceColor }}
              placeholder={t('agentSettings.shortcutsCustomEmojiPlaceholder')}
              maxLength={4}
            />
            <button
              onClick={() => {
                if (customEmoji) {
                  onChange(customEmoji)
                  onClose()
                }
              }}
              className="px-4 py-2 text-white rounded-lg text-[14px] font-medium hover:opacity-90"
              style={{ backgroundColor: aispaceColor }}
            >
              {t('agentSettings.shortcutsUseButton')}
            </button>
          </div>
        </div>
      )}
    </div>
  )
}

// Shortcut Modal Component - Slide-in Panel from Right
const ShortcutModal = ({ isOpen, onClose, onConfirm, initial = {}, taskId, aispaceColor }) => {
  const { t } = useTranslation()
  const [form, setForm] = useState({
    name: '',
    icon: '✨',
    description: '',
    display_order: 0,
    is_active: true,
  })
  const [showIconPicker, setShowIconPicker] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isVisible, setIsVisible] = useState(false)

  useEffect(() => {
    if (initial && initial.shortcut_id) {
      setForm({
        name: initial.name || '',
        icon: initial.icon || '✨',
        description: initial.description || '',
        display_order: initial.display_order || 0,
        is_active: initial.is_active !== undefined ? initial.is_active : true,
      })
    } else {
      setForm({
        name: '',
        icon: '✨',
        description: '',
        display_order: 0,
        is_active: true,
      })
    }
    setShowIconPicker(false)
  }, [initial, isOpen])

  // Handle slide-in animation and body scroll lock
  useEffect(() => {
    if (isOpen) {
      // Lock body scroll when modal is open
      document.body.style.overflow = 'hidden'
      // Small delay to trigger CSS transition
      requestAnimationFrame(() => {
        setIsVisible(true)
      })
    } else {
      setIsVisible(false)
    }

    // Cleanup - restore body scroll when modal closes
    return () => {
      document.body.style.overflow = ''
    }
  }, [isOpen])

  const handleClose = () => {
    setIsVisible(false)
    // Wait for animation to complete before actually closing
    setTimeout(() => {
      onClose()
    }, 300)
  }

  if (!isOpen) return null

  const handleSubmit = async () => {
    if (!form.name.trim() || !form.description.trim()) return
    setIsSubmitting(true)
    await onConfirm({ ...form, task_id: taskId })
    setIsSubmitting(false)
    handleClose()
  }

  return (
    <div className="fixed inset-0 z-50">
      {/* Backdrop */}
      <div
        className={`absolute inset-0 bg-black/50 transition-opacity duration-300 ${
          isVisible ? 'opacity-100' : 'opacity-0'
        }`}
        onClick={handleClose}
      />

      {/* Slide-in Panel */}
      <div
        className={`absolute top-0 right-0 h-full w-full max-w-[480px] bg-white shadow-2xl transform transition-transform duration-300 ease-out ${
          isVisible ? 'translate-x-0' : 'translate-x-full'
        }`}
      >
        <div className="flex flex-col h-full">
          {/* Header */}
          <div className="flex items-center justify-between px-6 py-5 border-b border-[#E5E7EB]">
            <h2 className="text-[18px] font-semibold text-[#111827]">
              {initial.shortcut_id ? t('agentSettings.shortcutsModalEditTitle') : t('agentSettings.shortcutsModalAddTitle')}
            </h2>
            <button
              onClick={handleClose}
              className="p-2 hover:bg-[#F3F4F6] rounded-lg transition-colors"
            >
              <X size={20} className="text-[#6B7280]" />
            </button>
          </div>

          {/* Form Content */}
          <div className="flex-1 overflow-y-auto px-6 py-6 space-y-6">
            {/* Shortcut Name */}
            <div>
              <label className="block text-[14px] font-medium text-[#374151] mb-2">
                {t('agentSettings.shortcutsNameLabel')}
              </label>
              <input
                type="text"
                className="w-full border border-[#E5E7EB] rounded-lg px-4 py-3 text-[14px] focus:outline-none focus:ring-2 focus:ring-offset-0 transition-all"
                style={{ '--tw-ring-color': aispaceColor }}
                value={form.name}
                onChange={e => setForm({ ...form, name: e.target.value })}
                placeholder={t('agentSettings.shortcutsNamePlaceholder')}
              />
            </div>

            {/* Icon */}
            <div>
              <label className="block text-[14px] font-medium text-[#374151] mb-2">
                {t('agentSettings.shortcutsIconLabel')}
              </label>
              <div className="relative">
                <button
                  type="button"
                  onClick={() => setShowIconPicker(!showIconPicker)}
                  className="flex items-center gap-3 w-full border border-[#E5E7EB] rounded-lg px-4 py-3 hover:bg-[#F9FAFB] transition-colors text-left"
                >
                  <span className="text-2xl">{form.icon || '✨'}</span>
                  <span className="text-[14px] text-[#6B7280]">{t('agentSettings.shortcutsIconChange')}</span>
                </button>
                {showIconPicker && (
                  <IconPicker
                    value={form.icon}
                    onChange={(icon) => setForm({ ...form, icon })}
                    onClose={() => setShowIconPicker(false)}
                    aispaceColor={aispaceColor}
                  />
                )}
              </div>
            </div>

            {/* Description */}
            <div>
              <label className="block text-[14px] font-medium text-[#374151] mb-2">
                {t('agentSettings.shortcutsDescriptionLabel')}
              </label>
              <textarea
                className="w-full border border-[#E5E7EB] rounded-lg px-4 py-3 text-[14px] focus:outline-none focus:ring-2 focus:ring-offset-0 transition-all resize-none"
                style={{ '--tw-ring-color': aispaceColor }}
                value={form.description}
                onChange={e => setForm({ ...form, description: e.target.value })}
                placeholder={t('agentSettings.shortcutsDescriptionPlaceholder')}
                rows="5"
              />
            </div>

            {/* Display Order */}
            <div>
              <label className="block text-[14px] font-medium text-[#374151] mb-2">
                {t('agentSettings.shortcutsOrderLabel')}
              </label>
              <input
                type="number"
                className="w-full border border-[#E5E7EB] rounded-lg px-4 py-3 text-[14px] focus:outline-none focus:ring-2 focus:ring-offset-0 transition-all"
                style={{ '--tw-ring-color': aispaceColor }}
                value={form.display_order}
                onChange={e => setForm({ ...form, display_order: parseInt(e.target.value) || 0 })}
                min="0"
              />
            </div>

            {/* Active Toggle */}
            <div className="flex items-center justify-between py-2">
              <span className="text-[14px] font-medium text-[#374151]">{t('agentSettings.shortcutsActiveLabel')}</span>
              <button
                type="button"
                onClick={() => setForm({ ...form, is_active: !form.is_active })}
                className={`relative w-12 h-6 rounded-full transition-colors ${
                  form.is_active ? '' : 'bg-[#E5E7EB]'
                }`}
                style={form.is_active ? { backgroundColor: aispaceColor } : {}}
              >
                <div className={`absolute top-0.5 w-5 h-5 bg-white rounded-full shadow-sm transition-transform ${
                  form.is_active ? 'left-[26px]' : 'left-0.5'
                }`} />
              </button>
            </div>
          </div>

          {/* Footer */}
          <div className="px-6 py-4 border-t border-[#E5E7EB] flex justify-end gap-3">
            <button
              onClick={handleClose}
              className="px-5 py-2.5 text-[14px] font-medium text-[#374151] bg-white border border-[#E5E7EB] rounded-lg hover:bg-[#F3F4F6] transition-colors"
            >
              {t('agentSettings.shortcutsCancel')}
            </button>
            <button
              onClick={handleSubmit}
              disabled={!form.name.trim() || !form.description.trim() || isSubmitting}
              className="px-5 py-2.5 text-[14px] font-medium text-white rounded-lg hover:opacity-90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              style={{ backgroundColor: aispaceColor }}
            >
              {isSubmitting ? (
                <>
                  <Loader2 size={16} className="animate-spin" />
                  <span>{t('agentSettings.shortcutsSaving')}</span>
                </>
              ) : (
                <span>{t('agentSettings.shortcutsSaveButton')}</span>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

const TaskShortcuts = () => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const { addToast, ToastContainer } = useToast()
  const { aispaceColor } = useThemeColor()

  const selectedTask = useSelector(selectSelectedTask)
  const { shortcuts, loading } = useSelector((state) => state.shortcuts)

  const [modalOpen, setModalOpen] = useState(false)
  const [editShortcut, setEditShortcut] = useState(null)
  const [deleteShortcutItem, setDeleteShortcutItem] = useState(null)
  const [draggedItem, setDraggedItem] = useState(null)
  const [localShortcuts, setLocalShortcuts] = useState([])
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false)

  // Fetch shortcuts when task changes
  useEffect(() => {
    if (selectedTask?.id) {
      dispatch(fetchShortcutsByTask({ taskId: selectedTask.id.toString(), includeInactive: true }))
    }
  }, [dispatch, selectedTask?.id])

  useEffect(() => {
    setLocalShortcuts(shortcuts)
  }, [shortcuts])

  const handleAdd = () => {
    setEditShortcut(null)
    setModalOpen(true)
  }

  const handleEdit = (shortcut) => {
    setEditShortcut(shortcut)
    setModalOpen(true)
  }

  const handleDelete = (shortcut) => {
    setDeleteShortcutItem(shortcut)
  }

  const handleModalConfirm = async (form) => {
    try {
      if (editShortcut) {
        await dispatch(updateShortcut({ ...form, shortcut_id: editShortcut.shortcut_id })).unwrap()
        addToast(t('agentSettings.shortcutsUpdateSuccess'), 'success')
      } else {
        await dispatch(createShortcut(form)).unwrap()
        addToast(t('agentSettings.shortcutsCreateSuccess'), 'success')
      }
      setModalOpen(false)
      dispatch(fetchShortcutsByTask({ taskId: selectedTask.id.toString(), includeInactive: true }))
    } catch (err) {
      addToast(t('agentSettings.shortcutsSaveError'), 'error')
    }
  }

  const handleDeleteConfirm = async () => {
    try {
      await dispatch(deleteShortcut(deleteShortcutItem.shortcut_id)).unwrap()
      addToast(t('agentSettings.shortcutsDeleteSuccess'), 'success')
      setDeleteShortcutItem(null)
      dispatch(fetchShortcutsByTask({ taskId: selectedTask.id.toString(), includeInactive: true }))
    } catch (err) {
      addToast(t('agentSettings.shortcutsDeleteError'), 'error')
    }
  }

  // Drag and drop handlers
  const handleDragStart = (e, index) => {
    setDraggedItem(index)
    e.dataTransfer.effectAllowed = 'move'
  }

  const handleDragOver = (e, index) => {
    e.preventDefault()
    if (draggedItem === null || draggedItem === index) return

    const newShortcuts = [...localShortcuts]
    const draggedShortcut = newShortcuts[draggedItem]
    newShortcuts.splice(draggedItem, 1)
    newShortcuts.splice(index, 0, draggedShortcut)

    const updatedShortcuts = newShortcuts.map((s, idx) => ({
      ...s,
      display_order: idx
    }))

    setLocalShortcuts(updatedShortcuts)
    setDraggedItem(index)
    setHasUnsavedChanges(true)
  }

  const persistOrder = async (orderArray) => {
    try {
      const orderData = orderArray.map((s, idx) => ({
        shortcut_id: s.shortcut_id,
        display_order: idx
      }))
      await dispatch(updateShortcutsOrder(orderData)).unwrap()
      addToast(t('agentSettings.shortcutsOrderSaveSuccess'), 'success')
      setHasUnsavedChanges(false)
      dispatch(fetchShortcutsByTask({ taskId: selectedTask.id.toString(), includeInactive: true }))
    } catch (err) {
      addToast(t('agentSettings.shortcutsOrderSaveError'), 'error')
    }
  }

  const handleDragEnd = () => {
    const wasDragging = draggedItem !== null
    setDraggedItem(null)
    if (!wasDragging) return
    // Persist the new order if it differs from the server-side order.
    const orderChanged = localShortcuts.some(
      (s, i) => shortcuts[i]?.shortcut_id !== s.shortcut_id
    )
    if (orderChanged) {
      persistOrder(localShortcuts)
    }
  }

  const handleSaveOrder = () => persistOrder(localShortcuts)

  const handleCancelChanges = () => {
    setLocalShortcuts(shortcuts)
    setHasUnsavedChanges(false)
  }

  if (!selectedTask) {
    return (
      <div className="flex flex-col items-center justify-center py-20 text-[#6B7280]">
        <div className="w-16 h-16 bg-[#F3F4F6] rounded-xl flex items-center justify-center mb-5">
          <Zap size={32} className="text-[#9CA3AF]" />
        </div>
        <p className="text-[14px]">{t('agentSettings.shortcutsSelectTaskHint')}</p>
      </div>
    )
  }

  return (
    <div>
      <ToastContainer />

      {/* Header */}
      <div className="mb-6">
        <h2 className="text-[20px] font-semibold text-[#111827]">{t('agentSettings.shortcutsHeaderTitle')}</h2>
        <p className="text-[14px] text-[#6B7280] mt-1">
          {t('agentSettings.shortcutsHeaderSubtitle')}
        </p>
      </div>

      {/* Shortcut List Card */}
      <div className="bg-[#F5F5F5] rounded-xl border border-[#EEEEEE] mb-6">
        {/* Card Header */}
        <div className="flex items-center justify-between p-6 border-b border-[#EEEEEE]">
          <h3 className="text-[16px] font-semibold text-[#111827]">{t('agentSettings.shortcutsListTitle')}</h3>
          <button
            onClick={handleAdd}
            className="flex items-center gap-2 px-4 py-2.5 text-white rounded-lg text-[14px] font-medium hover:opacity-90 transition-colors"
            style={{ backgroundColor: aispaceColor }}
          >
            <span>{t('agentSettings.shortcutsAddButton')}</span>
          </button>
        </div>

        {/* Shortcut Items */}
        <div className="p-6">
          {loading && localShortcuts.length === 0 ? (
            <div className="flex items-center justify-center py-16">
              <Loader2 className="w-8 h-8 animate-spin" style={{ color: aispaceColor }} />
            </div>
          ) : localShortcuts.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16">
              <div className="w-16 h-16 bg-[#F3F4F6] rounded-xl flex items-center justify-center mb-5">
                <Zap size={32} className="text-[#9CA3AF]" />
              </div>
              <h3 className="text-[16px] font-semibold text-[#111827] mb-2">
                {t('agentSettings.shortcutsEmptyTitle')}
              </h3>
              <p className="text-[14px] text-[#6B7280] text-center max-w-[320px]">
                {t('agentSettings.shortcutsEmptyHint')}
              </p>
            </div>
          ) : (
            <div className="space-y-3">
              {localShortcuts.map((shortcut, index) => (
                <div
                  key={shortcut.shortcut_id}
                  draggable
                  onDragStart={(e) => handleDragStart(e, index)}
                  onDragOver={(e) => handleDragOver(e, index)}
                  onDragEnd={handleDragEnd}
                  className={`flex items-center px-4 py-4 bg-white border border-[#EEEEEE] rounded-xl hover:border-[#D1D5DB] cursor-move transition-all ${
                    draggedItem === index ? 'border-[#9CA3AF] shadow-sm' : ''
                  } ${!shortcut.is_active ? 'opacity-50' : ''}`}
                >
                  {/* Drag Handle */}
                  <div className="mr-4 text-[#9CA3AF] cursor-grab">
                    <GripVertical size={18} />
                  </div>

                  {/* Icon */}
                  <div
                    className="flex-shrink-0 w-10 h-10 flex items-center justify-center rounded-full mr-4 text-xl bg-[#FEF3C7]"
                  >
                    {shortcut.icon || '✨'}
                  </div>

                  {/* Name and Description */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="text-[14px] font-semibold text-[#111827]">{shortcut.name}</span>
                      {!shortcut.is_active && (
                        <span className="px-2 py-0.5 text-[11px] font-medium bg-[#F3F4F6] text-[#6B7280] rounded">
                          {t('agentSettings.shortcutsInactiveBadge')}
                        </span>
                      )}
                    </div>
                    <p className="text-[14px] text-[#6B7280] truncate mt-0.5">{shortcut.description}</p>
                  </div>

                  {/* Actions */}
                  <div className="flex items-center gap-1 ml-4">
                    <button
                      onClick={() => handleEdit(shortcut)}
                      className="p-2 text-[#9CA3AF] hover:text-[#374151] hover:bg-[#F3F4F6] rounded-lg transition-colors"
                      title={t('agentSettings.shortcutsEditTooltip')}
                    >
                      <Settings size={18} />
                    </button>
                    <button
                      onClick={() => handleDelete(shortcut)}
                      className="p-2 text-[#9CA3AF] hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                      title={t('agentSettings.shortcutsDeleteTooltip')}
                    >
                      <Trash2 size={18} />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Info Box */}
      <div className="bg-[#ECFDF5] border border-[#A7F3D0] rounded-xl p-5">
        <div className="flex items-start gap-4">
          <div className="flex-shrink-0 w-10 h-10 flex items-center justify-center bg-[#D1FAE5] rounded-full">
            <Info size={20} className="text-[#059669]" />
          </div>
          <div>
            <h3 className="text-[14px] font-semibold text-[#065F46]">{t('agentSettings.shortcutsInfoTitle')}</h3>
            <p className="text-[14px] text-[#047857] mt-1 leading-relaxed">
              {t('agentSettings.shortcutsInfoText')}
            </p>
          </div>
        </div>
      </div>

      <ShortcutModal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        onConfirm={handleModalConfirm}
        initial={editShortcut || {}}
        taskId={selectedTask?.id?.toString()}
        aispaceColor={aispaceColor}
      />

      <DeleteModal
        isOpen={!!deleteShortcutItem}
        onClose={() => setDeleteShortcutItem(null)}
        onDelete={handleDeleteConfirm}
        title={t('agentSettings.shortcutsDeleteModalTitle')}
        message={t('agentSettings.shortcutsDeleteModalMessage', { name: deleteShortcutItem?.name })}
      />
    </div>
  )
}

export default TaskShortcuts
